import './index.scss';
import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { GraphCurve } from 'Types/Aggregates';
import { WindowSize, useWindowSize } from 'hooks/windowSize';

interface Props {
  dataset: GraphCurve[];
  isPDF?: boolean;
  eff: Array<number> | null | undefined;
}

export default function HeatReclaimCurve({ dataset, eff }: Props) {
  const windowSize: WindowSize = useWindowSize();

  const getMaxXValue = () => {
    let max: number = -999999999;
    if (dataset) {
      max = dataset?.reduce((curr: number, acc: GraphCurve) => {
        let localMax = curr;
        if (acc && acc.data) {
          acc.data.forEach((point) => {
            if (!!point?.x && point.x > localMax) localMax = point.x;
          });
        }
        return localMax;
      }, max);
    }
    return max;
  };

  const maxX: number = Math.ceil(getMaxXValue() / 1000) * 1000;
  const maxEff: number = eff ? Math.max(...eff) : 1;
  const minEff: number = eff ? Math.min(...eff) : 0.75;

  const ResultLayer = ({ series }: { series: any }) => {
    const size = 15;
    const borderWidth = 2;

    const resultSeries = series.filter((set: any) => set.id === 'result');

    if (resultSeries) {
      return (
        <>
          {resultSeries &&
            !!resultSeries.length &&
            resultSeries[0].data &&
            resultSeries[0].data.map((point: any) => (
              <g key={point.data.y}>
                <circle
                  fill="white"
                  r={size / 2}
                  cx={point.position.x}
                  cy={point.position.y}
                  strokeWidth={borderWidth}
                  stroke="black"
                />
              </g>
            ))}
        </>
      );
    }
    return null;
  };

  const styleById: { [key: string]: { [cssKey: string]: string | number } } = {
    dashed: {
      strokeDasharray: '12, 6',
      strokeWidth: 2,
    },
    vodka: {
      strokeDasharray: '1, 16',
      strokeWidth: 8,
      strokeLinejoin: 'round',
      strokeLinecap: 'round',
    },
    MaxPa: {
      strokeWidth: 4,
    },
    default: {
      strokeWidth: 2,
    },
  };

  const Line = ({
    series,
    lineGenerator,
    xScale,
    yScale,
  }: {
    series: any;
    xScale: any;
    yScale: any;
    lineGenerator: any;
  }) =>
    !!series?.length &&
    series.map(
      (set: { id: string; data: any; color: string }) =>
        !!set?.data?.length && (
          <path
            key={set.id}
            d={lineGenerator(
              set.data?.map((d: any) => ({
                x: xScale(d?.data?.x),
                y: yScale(d?.data?.y),
              }))
            )}
            fill="none"
            stroke={'green'}
            style={styleById[set.id] || styleById.default}
          />
        )
    );

  const getXGridValues = (): number[] => {
    const values: number[] = [];
    let incr: number = maxX / 20;
    if ((windowSize?.width || 9999999) < 768) {
      incr = maxX / 10;
    }
    incr = Math.ceil(incr / 200) * 200;
    for (let i = 0; i <= maxX; i += incr) {
      values.push(i);
    }
    return values;
  };

  const getXTickValues = () => {
    const values: number[] = [];
    let incr: number = maxX / 10;
    if ((windowSize?.width || 9999999) < 768) {
      incr = maxX / 5;
    }
    incr = Math.ceil(incr / 200) * 200;
    for (let i = 0; i <= maxX; i += incr) {
      values.push(i);
    }
    return values;
  };

  if (!dataset) {
    return <></>;
  }

  return (
    <ResponsiveLine
      layers={[
        'grid',
        'markers',
        'areas',
        Line,
        'slices',
        'axes',
        'points',
        ResultLayer,
      ]}
      data={dataset}
      colors={[
        '#ff0000',
        '#000000',
        '#000000',
        '#000000',
        '#000000',
        '#000000',
        '#000000',
      ]}
      animate={false}
      margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
      xScale={{
        type: 'linear',
        min: 0,
        max:
          maxX > 2000
            ? Math.ceil(maxX / 200) * 200
            : Math.ceil(maxX / 100) * 100, // rounde nearest 100 or 200
      }}
      yScale={{
        type: 'linear',
        min: minEff > 0.1 ? Math.floor(minEff * 20) * 5 : 75, // Round to nearest 5%
        max: maxEff > 0.9 ? 100 : 90,
      }}
      gridXValues={getXGridValues()}
      gridYValues={
        maxEff > 0.9
          ? [...Array(26).keys()].map((a) => a + 75)
          : [...Array(16).keys()].map((a) => a + 75)
      }
      axisLeft={{
        legend: '%',
        legendOffset: -50,
        legendPosition: 'middle',
        tickValues: maxEff > 0.9 ? 5 : 3,
      }}
      axisBottom={{
        legend: '㎥/h',
        legendOffset: 40,
        legendPosition: 'middle',
        tickValues: getXTickValues(),
      }}
      enablePoints={false}
      useMesh
      curve="monotoneX"
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
    />
  );
}
